import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { useDispatch } from "react-redux";
import { incrementItemCount } from "../../reducers/cartCounterReducer";
import jwtDecode from "jwt-decode";
import { validateMandatoryAddonsOption } from "./addonLogic";

export default function useFoodItemState(
  food,
  handleClose,
  orderType,
  initialSelections = {}
) {
  const [quantity, setQuantity] = useState(initialSelections.quantity || 1);
  const [selectedAddons, setSelectedAddons] = useState(
    initialSelections.addons || {}
  );

  const dispatch = useDispatch();

  // Build final payload
  function generateOrderJson() {
    const token = localStorage.getItem("gotasteClienttoken");
    const decoded = jwtDecode(token);
    const output = {
      userId: decoded.UserId,
      restaurantId: food.restaurantId,
      foodItemId: food.id,
      foodItemQuantity: quantity,
      instruction: "N/A",
      orderType,
      addOns: [],
    };

    for (const [addOnId, addOnData] of Object.entries(selectedAddons)) {
      const hasEnabledOption = Object.values(addOnData.options || {}).some(
        (opt) => opt.enabled
      );
      const isEnabled =
        addOnData.enabled === true ||
        hasEnabledOption ||
        (addOnData.quantity && addOnData.quantity > 0);

      if (!isEnabled) continue;

      const addOnDetails = food.addOns.find((a) => a.id === addOnId);

      // const addOnQuantity = addOnDetails.isCountableLinked
      //   ? quantity
      //   : addOnDetails.isCountable
      //   ? addOnData.quantity && addOnData.quantity > 0
      //     ? addOnData.quantity
      //     : Object.values(addOnData.options || {}).reduce(
      //         (sum, option) => sum + (option.quantity || 0),
      //         0
      //       )
      //   : addOnData.options
      //   ? Object.values(addOnData.options).reduce(
      //       (sum, option) => sum + (option.enabled ? 1 : 0),
      //       0
      //     )
      //   : addOnData.enabled
      //   ? 1
      //   : 0;

      const addOnQuantity = addOnDetails.isCountableLinked
        ? quantity
        : addOnDetails.isCountable
        ? addOnData.quantity && addOnData.quantity > 0
          ? addOnData.quantity
          : Object.values(addOnData.options || {}).reduce(
              (sum, option) => sum + (option.quantity || 0),
              0
            )
        : addOnData.options
        ? Object.values(addOnData.options).reduce(
            (sum, option) => sum + (option.enabled ? option.quantity || 0 : 0),
            0
          )
        : addOnData.enabled
        ? addOnData.quantity && addOnData.quantity > 0
          ? addOnData.quantity
          : 1
        : 0;

      const formattedAddOn = {
        addOnId,
        addOnName: addOnDetails?.addOnName || "",
        addOnQuantity,
        options: [],
      };

      for (const [optionId, optionData] of Object.entries(
        addOnData.options || {}
      )) {
        if (!optionData.enabled) continue;

        const optionDef = addOnDetails.options.find(
          (o) => o.optionId === optionId
        );
        // If addon is countable and option is not linked, use addon quantity
        const optionQuantity =
          addOnDetails.isCountable && !optionDef.isCountableLinked
            ? addOnData.quantity || 0
            : optionDef.isCountableLinked
            ? quantity
            : optionData.quantity || 0;

        const formattedOption = {
          optionId,
          optionQuantity,
          subOptionIds: [],
        };

        for (const [subOptionId, subOptionData] of Object.entries(
          optionData.subOptions || {}
        )) {
          if (!subOptionData.enabled) continue;

          formattedOption.subOptionIds.push({
            subOptionId,
            // Sub-option quantity matches parent option quantity
            subOptionQuantity: optionQuantity,
          });
        }
        formattedAddOn.options.push(formattedOption);
      }

      output.addOns.push(formattedAddOn);
    }

    return output;
  }

  const handleAddToCart = async () => {
    const token = localStorage.getItem("gotasteClienttoken");
    if (!token) {
    toast.warning("Please login !!");
    return;
  }
    const isMandatoryAddonsOption = validateMandatoryAddonsOption(
      food,
      selectedAddons
    );
    if (!isMandatoryAddonsOption) return;

    try {
      const finalPayload = generateOrderJson();

      const response = await axios.post(
        `${process.env.REACT_APP_SECRET}/api/Cart/InsertIntoCart`,
        finalPayload
      );

      if (response.data) {
        toast.success(response.data);
        dispatch(incrementItemCount(1));
        setTimeout(() => {
          handleClose();
        }, 1000);
      }
    } catch (error) {
      console.log("add to cart failed", error?.response?.data || error);
      if (error.response.data.status === 400 && error.response.status === 400) {
        toast.warning("all data is not available");
      } else {
        toast.warning(error.response.data);
      }
    }
  };

  const handleEditFoodItem = async (syncedData, selectedItemInCart) => {
    const isMandatoryAddonsOption = validateMandatoryAddonsOption(
      food,
      selectedAddons
    );
    if (!isMandatoryAddonsOption) return;

    try {
      const currentQty = selectedItemInCart?.foodItem?.foodItemQuantity || 1;

      if (quantity !== currentQty) {
        await axios.patch(
          `${process.env.REACT_APP_SECRET}/api/Cart/UpdateFoodItemQuantity/${selectedItemInCart.foodItem.cartItemId}?quantity=${quantity}`
        );
      }

      const payload = Object.entries(syncedData).map(([addOnId, addon]) => {
        const addonDef = food.addOns.find((a) => a.id === addOnId);
        return {
          addOnId,
          addOnName: addonDef?.addOnName || "",
          addOnQuantity: addon.quantity ?? (addon.enabled ? 1 : 0),
          options: Object.entries(addon.options || {}).map(
            ([optionId, option]) => {
              const optionDef = addonDef?.options?.find(
                (o) => o.optionId === optionId
              );
              // If addon is countable and option is not linked, use addon quantity
              const optionQuantity =
                addonDef.isCountable && !optionDef?.isCountableLinked
                  ? addon.quantity || 0
                  : optionDef?.isCountableLinked
                  ? quantity
                  : option.quantity || 0;

              return {
                optionId,
                optionName: optionDef?.optionName || "",
                optionQuantity,
                subOptions: Object.entries(option.subOptions || {}).map(
                  ([subOptionId, sub]) => {
                    const subOptionDef = optionDef?.subOptions?.find(
                      (so) => so.subOptionId === subOptionId
                    );
                    return {
                      subOptionId,
                      subOptionName: subOptionDef?.subOptionName || "",
                      // Sub-option quantity matches parent option quantity
                      subOptionQuantity: optionQuantity,
                    };
                  }
                ),
              };
            }
          ),
        };
      });

      const response = await axios.put(
        `${process.env.REACT_APP_SECRET}/api/Cart/UpdateCartItems?cartFoodItemId=${selectedItemInCart.foodItem.cartItemId}&subOrderId=${selectedItemInCart.foodItem.subOrderId}`,
        payload
      );

      if (response.data) {
        toast.success(response.data);
        dispatch(incrementItemCount(1));
        setTimeout(() => handleClose(), 1000);
      }
    } catch (err) {
      console.error("Edit failed", err?.response?.data || err);
      toast.error("Failed to update item");
    }
  };

  // Main Food Quantity
  const handleQuantityChange = (delta) => {
    setQuantity((prev) => Math.max(1, prev + delta));
  };

  const handleAddonQuantityChange = (addon, delta) => {
    setSelectedAddons((prev) => {
      const currentAddon = prev[addon.id] || {};
      const currentQty = currentAddon.quantity || 0;
      const min = addon.minQuantity ?? 1;
      const max = addon.maxQuantity ?? 99;
      const inc = addon.incrementalQuantity ?? addon.increamentalQuantity ?? 1;

      if (addon.isCountableLinked) {
        // For linked addons, toggle selection without changing quantity
        if (delta > 0 && !currentAddon.enabled) {
          // Select addon, quantity matches foodItemQuantity
          return {
            ...prev,
            [addon.id]: {
              ...currentAddon,
              enabled: true,
              quantity: quantity, // Match food item quantity
            },
          };
        } else if (delta < 0) {
          const newState = { ...prev };
          delete newState[addon.id];
          return newState;
        }

        return prev; // No change if already selected or deselected
      }
      // debugger
      let nextQty;
      if (currentQty === 0 && delta > 0) {
        // User clicked the initial "+"
        nextQty = min;
      } else {
        nextQty = currentQty + delta * inc;
      }

      if (nextQty > max) {
        nextQty = max;
      }

      // handle minimum quantity and deselect
      if (nextQty < min) {
        // If at minimum quantity, deselect the addon
        if (currentQty == min) {
          const newState = { ...prev };
          delete newState[addon.id];
          return newState;
        } else {
          // Set to minimum quantity
          nextQty = min;
        }
      }

      // Update option quantities to match addon quantity if not linked (when change adon qty same time sync option and sub opt qty)
      const updatedOptions = { ...currentAddon.options };
      if (addon.isCountable) {
        Object.keys(updatedOptions).forEach((optionId) => {
          const optionDef = food.addOns
            .find((a) => a.id === addon.id)
            ?.options.find((o) => o.optionId === optionId);
          if (
            optionDef &&
            !optionDef.isCountableLinked &&
            updatedOptions[optionId].enabled
          ) {
            updatedOptions[optionId] = {
              ...updatedOptions[optionId],
              quantity: nextQty,
              // Update sub-options to match addon quantity
              subOptions: Object.keys(
                updatedOptions[optionId].subOptions || {}
              ).reduce((acc, subOptionId) => {
                if (updatedOptions[optionId].subOptions[subOptionId].enabled) {
                  acc[subOptionId] = {
                    ...updatedOptions[optionId].subOptions[subOptionId],
                    quantity: nextQty,
                  };
                }
                return acc;
              }, {}),
            };
          }
        });
      }

      return {
        ...prev,
        [addon.id]: {
          ...currentAddon,
          quantity: nextQty,
          options: updatedOptions, // Apply updated options
        },
      };
    });
  };

  // Non-countable toggle
  const handleAddonToggle = (addon) => {
    setSelectedAddons((prev) => {
      const current = prev[addon.id];
      if (current?.enabled) {
        // Deselect
        const updated = { ...prev };
        delete updated[addon.id];
        return updated;
      }
      return {
        ...prev,
        [addon.id]: { enabled: true },
      };
    });
  };

  const handleRadioOptionToggle = (addonId, option, forceSelect = false) => {
    setSelectedAddons((prev) => {
      const addon = prev[addonId] || {};
      const currentOptions = addon.options || {};
      const isAlreadyChecked = currentOptions?.[option.optionId]?.enabled;
      const addonDef = food.addOns.find((a) => a.id === addonId);

      if (isAlreadyChecked && !forceSelect) {
        const updatedOptions = { ...currentOptions };
        delete updatedOptions[option.optionId];
        return {
          ...prev,
          [addonId]: {
            ...addon,
            options: updatedOptions,
          },
        };
      }
      //  For countable addons, option quantity should match addon quantity
      const optionQuantity = addonDef.isCountable
        ? addon.quantity || addonDef.minQuantity || 1 //  Use addon quantity or initialized minQuantity
        : option.isCountableLinked
        ? quantity
        : option.minQuantity || 1;

      //  Enable the parent addon when an option is selected
      // For countable addons, initialize quantity if not set
      const updatedAddon = {
        ...addon,
        enabled: true, //  Ensure addon is marked as enabled
        //  Initialize quantity for countable addons
        quantity:
          addonDef.isCountable && !addon.quantity
            ? addonDef.minQuantity || 1
            : addon.quantity, //  Set initial quantity for countable addon
        options: {
          [option.optionId]: {
            enabled: true,
            quantity: optionQuantity, // Use updated optionQuantity
          },
        },
      };

      return {
        ...prev,
        [addonId]: updatedAddon, //  Update the addon with enabled state and quantity
      };
    });
  };

  const handleCheckboxOptionToggle = (addonId, option, forceSelect = false) => {
    setSelectedAddons((prev) => {
      const addon = prev[addonId] || {};
      const currentOptions = addon.options || {};
      const isAlreadyChecked = currentOptions?.[option.optionId]?.enabled;
      const addonDef = food.addOns.find((a) => a.id === addonId);
      // debugger
      if (isAlreadyChecked && !forceSelect) {
        const updatedOptions = { ...currentOptions };
        delete updatedOptions[option.optionId];
        return {
          ...prev,
          [addonId]: {
            ...addon,
            options: updatedOptions,
          },
        };
      }
      //  For countable addons, option quantity should match addon quantity
      const optionQuantity = addonDef.isCountable
        ? addon.quantity || addonDef.minQuantity || 1 //  Use addon quantity or initialized minQuantity
        : option.isCountableLinked
        ? quantity
        : option.minQuantity || 1;

      // Enable the parent addon when an option is selected
      //  For countable addons, initialize quantity if not set
      const updatedAddon = {
        ...addon,
        enabled: true, // Ensure addon is marked as enabled
        // Initialize quantity for countable addons
        quantity:
          addonDef.isCountable && !addon.quantity
            ? addonDef.minQuantity || 1
            : addon.quantity, // Set initial quantity for countable addon
        options: {
          ...currentOptions,
          [option.optionId]: {
            enabled: true,
            quantity: optionQuantity, // Use updated optionQuantity
          },
        },
      };

      return {
        ...prev,
        [addonId]: updatedAddon, // Update the addon with enabled state and quantity
      };
    });
  };

  // Option quantity
  const handleOptionQuantityChange = (addonId, option, delta) => {
    setSelectedAddons((prev) => {
      const addon = prev[addonId] || {};
      const options = addon.options || {};
      const current = options[option.optionId] || {};
      const addonDef = food.addOns.find((a) => a.id === addonId);

      // If addon is countable and option is not linked, use addon quantity
      if (addonDef.isCountable && !option.isCountableLinked) {
        return {
          ...prev,
          [addonId]: {
            ...addon,
            options: {
              ...options,
              [option.optionId]: {
                ...current,
                enabled: true,
                quantity: addon.quantity || 0,
                // Update sub-options to match addon quantity
                subOptions: Object.keys(current.subOptions || {}).reduce(
                  (acc, subOptionId) => {
                    if (current.subOptions[subOptionId].enabled) {
                      acc[subOptionId] = {
                        ...current.subOptions[subOptionId],
                        quantity: addon.quantity || 0,
                      };
                    }
                    return acc;
                  },
                  {}
                ),
              },
            },
          },
        };
      }

      let newQty = (current.quantity || 0) + delta;
      if (newQty <= option.minQuantity && delta < 0) {
        newQty =
          current.quantity === option.minQuantity ? 0 : option.minQuantity;
      }

      if (newQty > option.maxQuantity) newQty = option.maxQuantity;

      return {
        ...prev,
        [addonId]: {
          ...addon,
          options: {
            ...options,
            [option.optionId]: {
              ...current,
              enabled: true,
              quantity: newQty,
            },
          },
        },
      };
    });
  };

  const handleSubOptionChange = (
    addonId,
    optionId,
    subOption,
    optionQuantity,
    forceSelect = false
  ) => {
    setSelectedAddons((prev) => {
      const addon = prev[addonId] || {};
      const options = addon.options || {};
      const optionState = options[optionId] || {};
      const currentSubOptions = optionState.subOptions || {};
      const isAlreadyChecked =
        currentSubOptions?.[subOption.subOptionId]?.enabled;

      if (isAlreadyChecked && !forceSelect) {
        // Deselect the sub-option if it's already selected and forceSelect is false
        return {
          ...prev,
          [addonId]: {
            ...addon,
            options: {
              ...options,
              [optionId]: {
                ...optionState,
                subOptions: {}, // Clear all sub-options to enforce single selection
              },
            },
          },
        };
      }

      // Only the selected sub-option is included, clearing any previous sub-options
      const updatedSubOptions = {
        [subOption.subOptionId]: {
          enabled: true,
          quantity: optionQuantity,
        },
      };

      return {
        ...prev,
        [addonId]: {
          ...addon,
          enabled: true, // Ensure addon is marked as enabled
          options: {
            ...options,
            [optionId]: {
              ...optionState,
              enabled: true, // Ensure parent option is marked as enabled
              subOptions: updatedSubOptions,
            },
          },
        },
      };
    });
  };

  // const handleSubOptionChange = (
  //   addonId,
  //   optionId,
  //   subOption,
  //   optionQuantity
  // ) => {
  //   setSelectedAddons((prev) => {
  //     const addon = prev[addonId] || {};
  //     const options = addon.options || {};
  //     const optionState = options[optionId] || {};

  //     const newSubOptions = {};

  //     newSubOptions[subOption.subOptionId] = {
  //       enabled: true,
  //       // Sub-option quantity matches parent option quantity
  //       quantity: optionQuantity,
  //     };

  //     return {
  //       ...prev,
  //       [addonId]: {
  //         ...addon,
  //         options: {
  //           ...options,
  //           [optionId]: {
  //             ...optionState,
  //             subOptions: newSubOptions,
  //           },
  //         },
  //       },
  //     };
  //   });
  // };

  return {
    quantity,
    selectedAddons,
    handleQuantityChange,
    handleAddonQuantityChange,
    handleAddonToggle,
    handleRadioOptionToggle,
    handleCheckboxOptionToggle,
    handleOptionQuantityChange,
    handleSubOptionChange,
    handleAddToCart,
    handleEditFoodItem,
  };
}
